'use client'
import {useMemo, type ReactNode} from 'react'
import Link from 'next/link'
// Hooks
import {useRouteParams} from '../hooks/use-route-params'
// Helpers
import {ensureTrailingSlash} from '../utils/string-helpers'

interface BoundLinkProps extends Omit<React.ComponentProps<typeof Link>, 'href'> {
  href: string
  children: ReactNode
}

const getSingleParamValue = (value: string | string[] | null): string | null => {
  if (!value) return null
  return Array.isArray(value) ? value[0] || null : value
}

export default function BoundLink({href, children, ...props}: BoundLinkProps) {
  const {organizationSlug, ballotSlug} = useRouteParams()

  const resolvedHref = useMemo(() => {
    const singleOrganizationSlug = getSingleParamValue(organizationSlug)
    const singleBallotSlug = getSingleParamValue(ballotSlug)

    let cleanHref = href
    if (cleanHref?.startsWith('/')) cleanHref = cleanHref.slice(1)
    if (cleanHref?.endsWith('/')) cleanHref = cleanHref.slice(0, -1)

    if (cleanHref.startsWith('o/')) {
      return ensureTrailingSlash(`/${cleanHref}`)
    }

    const boundHref = (singleOrganizationSlug && singleBallotSlug)
      ? (
        cleanHref
          ? `/o/${singleOrganizationSlug}/${singleBallotSlug}/${cleanHref}`
          : `/o/${singleOrganizationSlug}/${singleBallotSlug}`
      )
      : href

    return ensureTrailingSlash(boundHref)
  }, [
    href,
    organizationSlug,
    ballotSlug,
  ])

  return (
    <Link href={resolvedHref as string} {...props}>
      {children}
    </Link>
  )
}
